import { Link } from "react-router-dom";
import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";

export const PerformanceLab = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Performance Lab</h1>

        <div className="text-sm text-muted-foreground mb-8">
          <p>Analyze your trading performance with advanced analytics</p>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Overview</h2>
          <p className="text-muted-foreground mb-4">
            Performance Lab is a comprehensive trading analytics tool available from the main
            navigation. It provides detailed insights into your trading activity, helping you
            identify strengths, weaknesses, and opportunities for improvement.
          </p>
          <p className="text-muted-foreground">
            Access it from the &quot;Performance Lab&quot; link in the header navigation, or visit{" "}
            <Link to="/performance-lab" className="text-primary hover:underline">
              /performance-lab
            </Link>{" "}
            directly.
          </p>
        </section>

        {/* Analytics Tabs */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Analytics Tabs</h2>
          <p className="text-muted-foreground mb-6">
            Performance Lab organizes its analytics into several tabs. Each tab focuses on a
            different aspect of your trading:
          </p>
          <ul className="list-disc pl-6 space-y-3 text-muted-foreground">
            <li>
              <strong className="text-foreground">Overview</strong> — Summary of your key trading
              metrics and Trader Score at a glance.
            </li>
            <li>
              <strong className="text-foreground">Performance</strong> — Detailed performance
              charts and P&L distributions.
            </li>
            <li>
              <strong className="text-foreground">Calendar</strong> — Calendar view showing your
              daily trading P&L, making it easy to spot patterns.
            </li>
            <li>
              <strong className="text-foreground">Time Analysis</strong> — Analysis of your
              performance by hour of day and day of week, helping you identify your best and worst
              trading times.
            </li>
            <li>
              <strong className="text-foreground">Asset Analysis</strong> — Per-market breakdown of
              your trades, win rates, and P&L.
            </li>
            <li>
              <strong className="text-foreground">Behavior</strong> — Analysis of your trading
              patterns including long vs short tendencies, position sizing consistency, and
              overtrading detection.
            </li>
            <li>
              <strong className="text-foreground">Achievements</strong> — Milestones and
              achievements earned through your trading activity.
            </li>
          </ul>
        </section>

        {/* Trader Score */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Trader Score</h2>
          <p className="text-muted-foreground mb-4">
            Your Trader Score is a composite rating from 0–100 based on six dimensions displayed in
            a radar chart:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li>Win Rate (20% weight)</li>
            <li>Profit Factor (25% weight)</li>
            <li>Average Win/Loss ratio (15% weight)</li>
            <li>Recovery Factor (15% weight)</li>
            <li>Max Drawdown (15% weight)</li>
            <li>Consistency (10% weight)</li>
          </ul>

          <h3 className="text-lg font-semibold mb-4">Score Ranges</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>Poor — below 30</li>
            <li>Needs Work — 30–40</li>
            <li>Below Average — 40–50</li>
            <li>Average — 50–60</li>
            <li>Good — 60–70</li>
            <li>Very Good — 70–80</li>
            <li>Excellent — above 80</li>
          </ul>

          <p className="text-muted-foreground">
            The Trader Score requires at least 5 completed trades to calculate.
          </p>
        </section>

        {/* AI Performance Review */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">AI Performance Review</h2>
          <p className="text-muted-foreground mb-4">
            The Review tab offers an AI-powered performance analysis that evaluates your recent
            trading activity and provides personalized feedback.
          </p>

          <h3 className="text-lg font-semibold mb-4">Eligibility</h3>
          <p className="text-muted-foreground mb-6">
            You need at least 50 completed trades or $100,000 in trading volume within the last 30
            days to generate a review.
          </p>

          <h3 className="text-lg font-semibold mb-4">What the Review Covers</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>Performance summary and key metrics</li>
            <li>Your key strengths as a trader</li>
            <li>Areas that need improvement</li>
            <li>Actionable recommendations</li>
            <li>Comparison with your previous review (if applicable)</li>
          </ul>

          <p className="text-muted-foreground">
            Reviews can be generated once per period and are saved in your review history.
          </p>
        </section>

        {/* Key Metrics Explained */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Key Metrics Explained</h2>
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Win Rate</h3>
              <p className="text-sm text-muted-foreground">
                Percentage of trades that were profitable.
              </p>
            </div>
            <div className="p-4 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Profit Factor</h3>
              <p className="text-sm text-muted-foreground">
                Ratio of gross profits to gross losses. Above 1.0 means profitable overall.
              </p>
            </div>
            <div className="p-4 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Expectancy</h3>
              <p className="text-sm text-muted-foreground">
                Average amount you can expect to win or lose per trade.
              </p>
            </div>
            <div className="p-4 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Max Drawdown</h3>
              <p className="text-sm text-muted-foreground">
                The largest peak-to-trough decline in your account value.
              </p>
            </div>
            <div className="p-4 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Sharpe Ratio</h3>
              <p className="text-sm text-muted-foreground">
                Risk-adjusted return measure. Higher is better.
              </p>
            </div>
            <div className="p-4 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Sortino Ratio</h3>
              <p className="text-sm text-muted-foreground">
                Similar to Sharpe but only penalizes downside volatility.
              </p>
            </div>
          </div>
        </section>

        <DocsNavigation
          previous={{ title: "Charts & Analysis Tools", href: "/charts" }}
          next={{ title: "Wallet Security", href: "/wallet-security" }}
        />
      </div>
    </DocsLayout>
  );
};
